import { Component, OnInit, ViewChild } from "@angular/core";
import { MatSelectChange } from "@angular/material/select";
import { ActivatedRoute, Router } from "@angular/router";
import { HelicoptersService } from "../helicopters/helicopters.service";
import { LocalStorageService } from "../shared/local-storage.service";
import { HTlDocument } from "../shared/models/htlDocument.model";
import { HtlService } from "./htl.service";
import { MatDialog } from "@angular/material/dialog";
import { HttpClient } from "@angular/common/http";
import { HtlPasswordDialogComponent } from "./htl-password-dialog/htl-password-dialog.component";
import { EditQtbComponent } from "../edit-qtb/edit-qtb.component";
import { AppConfigType } from "../shared/models/appConfig.model";
import moment from "moment";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";

@Component({
    selector: "app-htl",
    templateUrl: "./htl.component.html",
    styleUrls: ["./htl.component.scss"],
    standalone: false,
})
export class HtlComponent implements OnInit {
    public helicopterOptions: { value: number; label: string }[] = [];
    public selectedHelicopter: number;
    public blocksList: { id: number; serialNumber: number; issueDate: string }[] = [];
    public blockSerialNumber: number;
    public selectedBlockId: number;
    public expandHtlDocumentPanel = false;
    public expandHoldItemListPanel = false;
    public expandShortTermPanel = false;
    public selectedHtlDocumentId: number;
    public pdfPreviewUrl: any;
    public helicopterString: string;
    public holdItemListUrl: string;
    public shortTermsListUrl: string;
    public hasSignature = false;
    public isEditedDocument = false;
    public documentApproved = false;
    public canApprove = false;

    public originalPdfPath: string;
    public currentPdfPath: string;

    public isSuperUser = false;

    public editQtb = false;

    public notes = [];
    public showHistoryPopup = false;

    @ViewChild("editqtbtable") editQtbTable: EditQtbComponent;

    public data = [];
    public htlDocuments: HTlDocument[] = [];

    private _userSession;

    constructor(
        private _dialog: MatDialog,
        private _router: Router,
        private _route: ActivatedRoute,
        private _http: HttpClient,
        private _snackBar: MatSnackBar,
        private _helicoptersService: HelicoptersService,
        private _htlService: HtlService,
        private _localStorageService: LocalStorageService,
        private _translate: TranslateService,
    ) {
        this._userSession = this._localStorageService.getUserLocalStorageObject();
    }

    ngOnInit(): void {
        if (this._userSession.appConfigs.find((a) => a.type === AppConfigType.approveDocument).enabled) {
            this.canApprove = true;
        }

        this._helicoptersService.getHelicopters(this._userSession.organizationId).subscribe((helicopters) => {
            helicopters.map((h) => {
                this.helicopterOptions.push({ value: h.id, label: h.registrationMark });
            });
            const presentQueryParams: any = this._route.snapshot.queryParams;
            this._getBlocksByHelicopterId(presentQueryParams.helicopter ? parseInt(presentQueryParams.helicopter) : helicopters[0].id, false)
                .then(() => {
                    this._route.queryParams.subscribe((queryParams: any) => {
                        if (!queryParams.helicopter) {
                            this._getBlocksByHelicopterId(this.helicopterOptions[0].value, true);
                        }
                        this.selectedBlockId = queryParams.block ? queryParams.block : null;
                        if (queryParams.block) {
                            this.blockSerialNumber = this.blocksList.filter((b) => b.id.toString() === queryParams.block)[0].serialNumber;
                        }
                        if (queryParams.helicopter) {
                            this.helicopterString = this._createHelicopterString(queryParams.helicopter);
                        }
                        this.selectedHtlDocumentId = queryParams.document;
                        this.editQtb = false;
                        if (!queryParams.document) {
                            this.htlDocuments = [];
                            this.expandHtlDocumentPanel = false;
                        } else {
                            this.expandHtlDocumentPanel = true;
                            this.setCurrentDocInfo();
                        }
                        this.expandHoldItemListPanel = queryParams.holdItemList ? true : false;
                        if (!queryParams.holdItemList) {
                            this.holdItemListUrl = null;
                        }

                        this.expandShortTermPanel = queryParams.shortTermsList ? true : false;
                        if (!queryParams.shortTermsList) {
                            this.shortTermsListUrl = null;
                        }
                    });
                })
                .catch(() => {
                    this._router.navigate(["."], {
                        relativeTo: this._route,
                        queryParams: { helicopter: helicopters[0].id },
                        queryParamsHandling: "preserve",
                    });
                });
        });
        if (this._localStorageService.getUserLocalStorageObject().htlAccountTypes.length > 0) {
            switch (this._localStorageService.getUserLocalStorageObject().htlAccountTypes[0].id) {
                case 1:
                    this.isSuperUser = true;
                    break;
            }
        }
    }

    onOpenDocumentNewTab() {
        window.open(this.originalPdfPath, "_blank");
    }

    onOpenDocumentPDFNewTab() {
        window.open(this.currentPdfPath, "_blank");
    }

    onOpenBlockNewTab() {
        const block = this.blocksList.find((b) => b.serialNumber === this.blockSerialNumber);
        if (!block.id || !this.selectedHelicopter) {
            console.log("Please select a helicopter and a block in order to export flights data");
            return;
        }
        this._htlService.generateHtlBlockPdf(this.selectedHelicopter, this.selectedBlockId).subscribe({
            next: (res) => window.open(res.downloadLink, "_blank"),
            error: () => console.log("Unable to download block"),
        });
    }

    onOpenHoldItemListNewTab() {
        this._htlService.generateHoltItemListDocumentPdf(this.selectedHelicopter, this.selectedBlockId).subscribe((result) => {
            window.open(result.downloadLink, "_blank");
        });
    }
    onOpenShortTermsListNewTab() {
        this._htlService.generateShortTermsDocumentPdf(this.selectedHelicopter, this.selectedBlockId).subscribe((result) => {
            window.open(result.downloadLink, "_blank");
        });
    }

    onHelicopterSelectChange(event: MatSelectChange) {
        this._getBlocksByHelicopterId(event.value, true);
    }

    onExpansionPanelOpen(panelName: string, open: boolean) {
        switch (panelName) {
            case "holdItemList":
                if (open) {
                    this._htlService.generateHoltItemListDocumentPdf(this.selectedHelicopter, this.selectedBlockId).subscribe((result) => {
                        this.holdItemListUrl = result.downloadLink;
                        this._router.navigate(["."], { relativeTo: this._route, queryParams: { holdItemList: "show" }, queryParamsHandling: "merge" }).then(() => {
                            this.pdfPreviewUrl = {
                                url: this.holdItemListUrl,
                                withCredentials: true,
                            };
                        });
                    });
                } else {
                    this.holdItemListUrl = null;
                    this._router.navigate(["."], { relativeTo: this._route, queryParams: { holdItemList: null }, queryParamsHandling: "merge" });
                }
                break;
            case "shortTerm":
                if (open) {
                    this._htlService.generateShortTermsDocumentPdf(this.selectedHelicopter, this.selectedBlockId).subscribe((result) => {
                        this.shortTermsListUrl = result.downloadLink;
                        this._router
                            .navigate(["."], {
                                relativeTo: this._route,
                                queryParams: {
                                    shortTermsList: "show",
                                },
                                queryParamsHandling: "merge",
                            })
                            .then(() => {
                                this.pdfPreviewUrl = {
                                    url: this.shortTermsListUrl,
                                    withCredentials: true,
                                };
                            });
                    });
                } else {
                    this.shortTermsListUrl = null;
                    this._router.navigate(["."], { relativeTo: this._route, queryParams: { shortTermsList: null }, queryParamsHandling: "merge" });
                }
                break;
            default:
                if (open) {
                    this._htlService.getHtlDocumentsByBlockId(this.selectedBlockId, 1).subscribe((htlDocuments) => {
                        this.htlDocuments = htlDocuments;
                        this.setCurrentDocInfo();
                    });
                } else {
                    this._router.navigate(["."], { relativeTo: this._route, queryParams: { document: null }, queryParamsHandling: "merge" });
                    this.htlDocuments = [];
                }
                break;
        }
    }

    private _createHelicopterString(helicopterId: string): string {
        const helicopterObj = this.helicopterOptions.filter((h) => h.value.toString() === helicopterId)[0];
        return helicopterObj.value + "-" + helicopterObj.label;
    }

    private _getBlocksByHelicopterId(helicopterId: number, forseRedirect: boolean): Promise<void> {
        return new Promise((resolve, reject) => {
            this.blocksList = [];
            this.selectedHelicopter = helicopterId;
            const queryParams = forseRedirect
                ? {
                      helicopter: helicopterId,
                      block: null,
                      document: null,
                      shortTermsList: null,
                      holdItemList: null,
                  }
                : { helicopter: helicopterId };
            this._router.navigate(["."], { relativeTo: this._route, queryParams, queryParamsHandling: "merge" });
            this._htlService.getHelicopterHtlBlocks(this.selectedHelicopter.toString()).subscribe(
                (blocks) => {
                    blocks.map((b) => {
                        this.blocksList.push({ id: b.id, serialNumber: b.blockSerialNumber, issueDate: b.issueDate });
                        resolve();
                    });
                    this.blocksList.reverse();
                },
                (error) => {
                    reject();
                },
            );
        });
    }

    public onEditButtonClick() {
        this._htlService.checkAllowedToEdit(this.selectedHelicopter, this.selectedHtlDocumentId).subscribe({
            next: () => {
                const dialogRef = this._dialog.open(HtlPasswordDialogComponent, {
                    disableClose: true,
                    width: "300px",
                    data: { allowed: true },
                });

                dialogRef.afterClosed().subscribe((res) => {
                    if (res) {
                        this.editQtb = true;
                        this.editQtbTable.enableEditing();
                    }
                });
            },
            error: (error) => {
                const dialogRef = this._dialog.open(HtlPasswordDialogComponent, {
                    disableClose: true,
                    width: "300px",
                    data: { allowed: false, message: error.error.message },
                });

                dialogRef.afterClosed().subscribe((res) => {
                    if (res) {
                        this.editQtb = true;
                        this.editQtbTable.enableEditing();
                    }
                });
            },
        });
    }

    public onSpreadsheetSave() {
        this.editQtb = false;
    }

    public getOriginalPdfPath(event) {
        this.originalPdfPath = event;
    }

    public getCurrentPdfPath(event) {
        this.currentPdfPath = event;
    }

    public getNotes(event) {
        this.notes = event;
    }

    /*
     * Method called when page is loaded and when current document is changed.
     * Sets info necessary for "show original" and "edit document" buttons.
     * Also sets the document pdf and xlsx paths and loads the excel spreadsheet
     */
    setCurrentDocInfo() {
        this.hasSignature = false;
        this.isEditedDocument = false;

        const document = this.htlDocuments.filter((doc: any) => doc.htlDocumentId == this.selectedHtlDocumentId)[0];

        if (document?.signatureDate) {
            this.hasSignature = true;
        }

        if (document?.isEdited) {
            this.isEditedDocument = true;
        }

        if (document?.isApproved) {
            this.documentApproved = true;
        } else {
            this.documentApproved = false;
        }
    }

    updateEditing(event) {
        this.editQtb = event;
    }

    viewHistory() {
        this.showHistoryPopup = !this.showHistoryPopup;
    }

    approveDocument() {
        this._htlService.approveDocument(this.selectedHtlDocumentId).subscribe(
            (res) => {
                this._snackBar.open("Document approved successfully.", this._translate.instant("GENERAL.close"), {
                    duration: 3000,
                });
                this.documentApproved = true;
                const doc = this.htlDocuments.find((h) => h.htlDocumentId == this.selectedHtlDocumentId);
                doc.isApproved = true;
                doc.approvedAt = moment().format();
                doc.approvedBy = this._userSession.userName;
            },
            (err) => {
                this._snackBar.open("There was an error approving the document.", this._translate.instant("GENERAL.close"), {
                    duration: 3000,
                });
            },
        );
    }
}
